import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Quote, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useLang } from '@/context/LanguageContext';
import { useServices } from '@/context/ServicesContext';
import { useOffer } from '@/context/OfferContext';
import ServiceCard from '@/components/ServiceCard';
import { ScrollReveal, NeonDivider } from '@/components/common/Primitives';
import { StatsCounter } from '@/components/common/StatsCounter';
import Terminal from '@/components/Terminal';
import { Icon } from '@/components/icons';
import { ServiceVisual, TechMarquee, WindowChrome } from '@/components/visuals/ITVisuals';
import GlobalThreatSection from '@/components/wow/GlobalThreatSection';
import EnterpriseHero from '@/components/experience/EnterpriseHero';
import api from '@/lib/api';

export default function Home() {
  const { t, tv } = useLang();
  const { services } = useServices();
  const { openOffer } = useOffer();
  const [meta, setMeta] = useState({ stats: [], testimonials: [], certifications: [], process: [], faq: [], comparison: [] });
  const platformSlugs = ['hosting', 'netzwerk', 'softwareentwicklung', 'sicherheit'];
  const [tab, setTab] = useState('hosting');

  useEffect(() => { api.get('/stats').then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const activeService = services.find((s) => s.slug === tab);

  return (
    <div className="itw-noise">
      {/* HERO — scroll-driven enterprise assembly */}
      <EnterpriseHero />

      {/* TECH MARQUEE */}
      <section className="relative z-10 border-y border-[color:var(--itw-border)] bg-[color:var(--itw-surface)]/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-6 py-4">
            <span className="hidden md:block shrink-0 text-xs uppercase tracking-widest text-[color:var(--itw-text-3)] font-mono">{t('sections.techTitle')}</span>
            <div className="flex-1 min-w-0"><TechMarquee /></div>
          </div>
        </div>
      </section>

      {/* PLATFORM SHOWCASE (interactive) */}
      <section id="platform" className="relative z-10 py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('sections.platformTitle')}</h2>
              <p className="mt-3 text-[color:var(--itw-text-2)]">{t('sections.platformSubtitle')}</p>
            </div>
          </ScrollReveal>
          <div className="mt-8 flex flex-wrap gap-2">
            {platformSlugs.map((slug) => {
              const s = services.find((x) => x.slug === slug);
              if (!s) return null;
              const active = tab === slug;
              return (
                <button key={slug} onClick={() => setTab(slug)} data-testid={`platform-tab-${slug}`}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium transition-all border ${active ? 'text-[color:var(--itw-bg)]' : 'text-[color:var(--itw-text-2)] border-[color:var(--itw-border)] hover:border-[color:var(--itw-border-strong)]'}`}
                  style={active ? { backgroundColor: s.accent, borderColor: s.accent, boxShadow: `0 0 24px ${s.accent}55` } : {}}>
                  <Icon name={s.icon} className="h-4 w-4" /> {tv(s.name)}
                </button>
              );
            })}
          </div>
          {activeService && (
            <div className="mt-8 grid lg:grid-cols-2 gap-8 items-center">
              <motion.div key={tab + '-v'} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                <ServiceVisual slug={tab} accent={activeService.accent} />
              </motion.div>
              <motion.div key={tab + '-t'} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
                <div className="font-mono text-xs uppercase tracking-[0.3em]" style={{ color: activeService.accent }}>// {activeService.slug}</div>
                <h3 className="mt-3 text-2xl sm:text-3xl font-semibold text-[color:var(--itw-text)]">{tv(activeService.name)}</h3>
                <p className="mt-3 text-[color:var(--itw-text-2)] leading-relaxed">{tv(activeService.intro)}</p>
                <ul className="mt-5 space-y-2.5">
                  {tv(activeService.outcomes).map((o, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-[color:var(--itw-text)]">
                      <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: activeService.accent }} />{o}
                    </li>
                  ))}
                </ul>
                <a href={`/leistungen/${tab}`} data-testid={`platform-explore-${tab}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium" style={{ color: activeService.accent }}>
                  {t('sections.platformExplore')} <ArrowRight className="h-4 w-4" />
                </a>
              </motion.div>
            </div>
          )}
        </div>
      </section>

      {/* GLOBAL THREAT INTELLIGENCE — WOW: live cyber defense globe + NOC */}
      <GlobalThreatSection />

      {/* SERVICES BENTO */}
      <section id="services" className="relative z-10 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('sections.servicesTitle')}</h2>
              <p className="mt-3 text-[color:var(--itw-text-2)]">{t('sections.servicesSubtitle')}</p>
            </div>
          </ScrollReveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((s, i) => (
              <ScrollReveal key={s.slug} delay={(i % 4) * 0.05}>
                <ServiceCard service={s} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* STATS + LIVE PANEL */}
      <section className="relative z-10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <ScrollReveal>
              <div className="grid grid-cols-2 gap-6">
                {(meta.stats || []).map((s, i) => (
                  <div key={i} className="glass rounded-2xl p-6"><StatsCounter value={s.value} label={tv(s.label)} /></div>
                ))}
              </div>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <WindowChrome label="status.it-welt360.net /live" accent="#2EF2A5">
                <div className="space-y-2.5">
                  {[['Frankfurt DC', 'operational', '99.99%'], ['Netzwerk-Backbone', 'operational', '12ms'], ['Backup-Cluster', 'operational', 'synced'], ['Security-Monitoring', 'operational', '0 threats']].map(([n, s, v]) => (
                    <div key={n} className="flex items-center justify-between rounded-lg bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] px-3 py-2.5 text-[13px] font-mono">
                      <span className="flex items-center gap-2 text-[color:var(--itw-text-2)]"><span className="h-2 w-2 rounded-full bg-[#2EF2A5] animate-blink" />{n}</span>
                      <span className="text-[color:var(--itw-text-3)]">{v}</span>
                    </div>
                  ))}
                </div>
              </WindowChrome>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* WHY US - comparison */}
      <section className="relative z-10 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('sections.whyTitle')}</h2>
              <p className="mt-3 text-[color:var(--itw-text-2)]">{t('sections.whySubtitle')}</p>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <div className="mt-10 glass rounded-2xl overflow-hidden">
              <div className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-5 sm:px-8 py-4 border-b border-[color:var(--itw-border)] text-sm">
                <span className="text-[color:var(--itw-text-3)]"></span>
                <span className="w-24 text-center font-semibold text-[color:var(--itw-cyan)]">{t('sections.whyUs')}</span>
                <span className="w-24 text-center text-[color:var(--itw-text-3)]">{t('sections.whyThem')}</span>
              </div>
              {(meta.comparison || []).map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-5 sm:px-8 py-3.5 border-b border-[color:var(--itw-border)] last:border-0">
                  <span className="text-sm text-[color:var(--itw-text)]">{tv(row.label)}</span>
                  <span className="w-24 grid place-items-center">
                    {row.us ? <span className="h-7 w-7 rounded-full grid place-items-center bg-[color:var(--itw-cyan)]/15 border border-[color:var(--itw-border-strong)]"><Check className="h-4 w-4 text-[color:var(--itw-cyan)]" /></span> : <X className="h-4 w-4 text-[color:var(--itw-text-3)]" />}
                  </span>
                  <span className="w-24 grid place-items-center">
                    {row.them ? <Check className="h-4 w-4 text-[color:var(--itw-text-2)]" /> : <X className="h-4 w-4 text-[#FF5C7A]/70" />}
                  </span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* PROCESS */}
      <section className="relative z-10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-2xl">
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('sections.processTitle')}</h2>
              <p className="mt-3 text-[color:var(--itw-text-2)]">{t('sections.processSubtitle')}</p>
            </div>
          </ScrollReveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {(meta.process || []).map((p, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="glass rounded-2xl p-6 h-full relative overflow-hidden">
                  <div className="font-mono text-5xl font-semibold text-[color:var(--itw-cyan)]/20">{p.num}</div>
                  <h3 className="mt-3 font-display text-lg font-semibold text-[color:var(--itw-text)]">{tv(p.title)}</h3>
                  <p className="mt-2 text-sm text-[color:var(--itw-text-2)] leading-relaxed">{tv(p.text)}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative z-10 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--itw-text)] text-center">{t('sections.testimonialsTitle')}</h2>
          </ScrollReveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {(meta.testimonials || []).map((tm, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <div className="glass rounded-2xl p-6 h-full">
                  <Quote className="h-7 w-7 text-[color:var(--itw-cyan)]/40" />
                  <p className="mt-3 text-sm text-[color:var(--itw-text)] leading-relaxed">{tv(tm.text)}</p>
                  <div className="mt-5 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[color:var(--itw-surface-2)] border border-[color:var(--itw-border)] grid place-items-center font-display font-semibold text-[color:var(--itw-cyan)]">{tm.name[0]}</div>
                    <div>
                      <div className="text-sm font-medium text-[color:var(--itw-text)]">{tm.name}</div>
                      <div className="text-xs text-[color:var(--itw-text-3)]">{tv(tm.role)}</div>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal delay={0.1}>
            <div className="mt-14 text-center">
              <p className="text-xs uppercase tracking-widest text-[color:var(--itw-text-3)]">{t('sections.certsTitle')}</p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                {(meta.certifications || []).map((c) => (
                  <span key={c} className="glass rounded-full px-4 py-2 text-sm text-[color:var(--itw-text-2)] border border-[color:var(--itw-border)]">{c}</span>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[color:var(--itw-text)] text-center mb-10">{t('sections.faqTitle')}</h2>
          </ScrollReveal>
          <Accordion type="single" collapsible className="space-y-3" data-testid="home-faq">
            {(meta.faq || []).map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="glass rounded-xl border border-[color:var(--itw-border)] px-5">
                <AccordionTrigger className="text-left text-[color:var(--itw-text)] hover:no-underline">{tv(f.q)}</AccordionTrigger>
                <AccordionContent className="text-[color:var(--itw-text-2)] leading-relaxed">{tv(f.a)}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="relative rounded-3xl overflow-hidden bg-[color:var(--itw-surface)] border border-[color:var(--itw-border-strong)] p-10 sm:p-16 text-center neon-edge">
              <div className="absolute inset-0 itw-grid opacity-40" />
              <div className="relative">
                <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-[color:var(--itw-text)]">{t('sections.ctaBandTitle')}</h2>
                <p className="mt-4 text-[color:var(--itw-text-2)] max-w-xl mx-auto">{t('sections.ctaBandText')}</p>
                <div className="mt-8 flex justify-center">
                  <Button onClick={() => openOffer('')} data-testid="cta-band-button"
                    className="h-12 px-8 bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity rounded-full text-base">
                    {t('hero.ctaPrimary')} <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
